/**
 * The seam between the tasting dashboard (W4) and the admin registration UI
 * (W2). W4 renders these; W2 owns this file and replaces the placeholders with
 * the real dialogs (register someone on their behalf; correct a person's wine,
 * category and payment). Props are the contract — keep them stable. See
 * docs/adr/002-tier2-contract.md.
 */
import { useState } from 'react';
import type { TastingDashboardRow } from '@gwc/shared';
import { RegistrationEditorDialog } from '../RegistrationEditorDialog';

export interface RegistrationActionsProps {
  eventId: string;
  row: TastingDashboardRow;
  /** Call after any successful change so the dashboard refetches. */
  onChanged: () => void;
}

/** Per-row admin actions: edit wine / category / payment / status. */
export function RegistrationActions({ eventId, row, onChanged }: RegistrationActionsProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn-ghost px-3 py-1 text-xs"
        aria-label={`Edit ${row.firstName} ${row.lastName}'s registration`}
        onClick={() => setOpen(true)}
      >
        Edit
      </button>
      {open && (
        <RegistrationEditorDialog
          open
          onClose={() => setOpen(false)}
          eventId={eventId}
          registrationId={row.registrationId}
          onSaved={onChanged}
        />
      )}
    </>
  );
}

export interface RegisterSomeoneButtonProps {
  eventId: string;
  onChanged: () => void;
}

/** "Register someone" — a member who phoned in, or a guest added as a person. */
export function RegisterSomeoneButton({ eventId, onChanged }: RegisterSomeoneButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
        Register someone
      </button>
      {open && (
        <RegistrationEditorDialog
          open
          onClose={() => setOpen(false)}
          eventId={eventId}
          onSaved={onChanged}
        />
      )}
    </>
  );
}
